import React, { useState, useEffect } from 'react';
import { useParams, Link, useNavigate } from 'react-router-dom';
import { useLanguage } from '../context/LanguageContext';
import { SEO } from '../components/SEO';
import { Header } from '../components/Header';
import { Footer } from '../components/Footer';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import { 
  Clock, MapPin, Check, X, ChevronLeft, ChevronRight, 
  MessageCircle, Send, Calendar, Users 
} from 'lucide-react';
import { tours, testimonials } from '../utils/mockData';
import { useToast } from '../hooks/use-toast';
import { sendItineraryRequest } from '../utils/emailService';

export const TourDetailPage = () => {
  const { slug } = useParams();
  const navigate = useNavigate();
  const { language, t } = useLanguage();
  const { toast } = useToast();
  
  const [tour, setTour] = useState(null);
  const [currentImageIndex, setCurrentImageIndex] = useState(0);
  const [bookingForm, setBookingForm] = useState({
    name: '',
    email: '',
    phone: '',
    dates: '',
    travelers: '2',
    message: ''
  });

  useEffect(() => {
    // Scroll to top whenever the slug changes (new tour loaded)
    window.scrollTo({ top: 0, behavior: 'smooth' });
    
    const foundTour = tours.find(t => t.slug === slug);
    if (foundTour) {
      setTour(foundTour);
      // Update page title and meta
      document.title = foundTour.metaTitle;
      const metaDesc = document.querySelector('meta[name="description"]');
      if (metaDesc) {
        metaDesc.setAttribute('content', foundTour.metaDescription);
      }
    } else {
      navigate('/tours');
    }
  }, [slug, navigate]);

  if (!tour) return null;

  const getWhatsAppBookingLink = () => {
    const tourName = language === 'en' ? tour.name : tour.nameEs;
    const message = bookingForm.name 
      ? `Hi! I'm ${bookingForm.name}. I'd like to book ${tourName}. Travel dates: ${bookingForm.dates || 'Flexible'}. Number of travelers: ${bookingForm.travelers}. ${bookingForm.message}`
      : `Hi! I'm interested in booking ${tourName}. Please share the booking details.`;
    return `https://wa.me/917827884419?text=${encodeURIComponent(message)}`;
  };

  const handleBookingSubmit = async (e) => {
    e.preventDefault();
    
    // Prepare submission data
    const submission = {
      ...bookingForm,
      tourSlug: tour.slug,
      tourName: language === 'en' ? tour.name : tour.nameEs,
    };
    
    try {
      // Send email via backend
      await sendItineraryRequest(submission);

      // Show success message
      toast({
        title: "Thanks! We received your request.",
        description: "We reply within 24 hours. Meanwhile, chat with us on WhatsApp.",
        duration: 6000,
      });

      // Reset form
      setBookingForm({
        name: '',
        email: '',
        phone: '',
        dates: '',
        travelers: '2',
        message: ''
      });
    } catch (error) {
      // Show error message
      toast({
        title: "Failed to send request",
        description: "Please try again or contact us via WhatsApp.",
        variant: "destructive",
        duration: 6000,
      });
      console.error('Failed to send email:', error);
    }
  };

  const handleFormChange = (e) => {
    const { name, value } = e.target;
    setBookingForm(prev => ({ ...prev, [name]: value }));
  };

  const nextImage = () => {
    setCurrentImageIndex((prev) => (prev + 1) % tour.gallery.length);
  };

  const prevImage = () => {
    setCurrentImageIndex((prev) => (prev - 1 + tour.gallery.length) % tour.gallery.length);
  };

  const relatedToursList = tours.filter(t => tour.relatedTours.includes(t.id));

  // JSON-LD Structured Data (price removed as per requirement)
  const jsonLd = {
    "@context": "https://schema.org",
    "@type": "TouristTrip",
    "name": tour.name,
    "description": tour.summary,
    "duration": tour.duration,
    "itinerary": tour.itinerary.map(day => ({
      "@type": "TouristAttraction",
      "name": language === 'en' ? day.title : day.titleEs,
      "description": language === 'en' ? day.description : day.descriptionEs
    })),
    "provider": {
      "@type": "TravelAgency",
      "name": "Peace of Mind Trips and Travels",
      "telephone": "+917827884419",
      "email": "contact@pomtrips.com",
      "url": "https://pomtrips.com"
    },
    "offers": {
      "@type": "Offer",
      "availability": "https://schema.org/InStock",
      "priceSpecification": {
        "@type": "PriceSpecification",
        "priceCurrency": "USD"
      }
    }
  };

  return (
    <div className="min-h-screen bg-[#FAF9F7]">
      <SEO 
        title={tour.metaTitle || `${language === 'en' ? tour.name : tour.nameEs} | Peace of Mind Trips`}
        description={tour.metaDescription || `Explore ${language === 'en' ? tour.name : tour.nameEs} with Peace of Mind Trips through thoughtfully curated experiences focused on culture, comfort, and meaningful travel.`}
        canonical={`/tours/${tour.slug}`}
      />
      
      {/* JSON-LD */}
      <script type="application/ld+json" dangerouslySetInnerHTML={{ __html: JSON.stringify(jsonLd) }} />

      <Header onRequestItinerary={() => document.getElementById('booking-form')?.scrollIntoView({ behavior: 'smooth' })} />

      {/* Hero Section */}
      <section className="relative h-[70vh] md:h-[80vh] overflow-hidden">
        <img
          src={tour.heroImage}
          alt={`${language === 'en' ? tour.name : tour.nameEs} - POMTrips`}
          className="w-full h-full object-cover"
        />
        <div className="absolute inset-0 bg-gradient-to-t from-black/70 via-black/30 to-transparent"></div>
        
        <div className="absolute inset-0 flex items-end">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 pb-16 w-full">
            <div className="max-w-3xl">
              <div className="flex items-center space-x-4 mb-4">
                <span className="inline-flex items-center px-4 py-2 bg-white/10 backdrop-blur-md rounded-full text-white text-sm">
                  <Clock className="h-4 w-4 mr-2" />
                  {language === 'en' ? tour.duration : tour.durationEs}
                </span>
              </div>
              
              <h1 className="font-serif text-4xl sm:text-5xl md:text-6xl text-white mb-4">
                {language === 'en' ? tour.name : tour.nameEs}
              </h1>
              
              <p className="text-xl text-white/90 italic mb-8">
                {language === 'en' ? tour.tagline : tour.taglineEs}
              </p>

              {/* Hero CTAs */}
              <div className="flex flex-wrap gap-4">
                <Button
                  onClick={() => document.getElementById('booking-form')?.scrollIntoView({ behavior: 'smooth' })}
                  className="bg-[#C4B5A0] hover:bg-[#6B4E37] text-white px-8 py-6 text-lg"
                >
                  Request Itinerary
                </Button>
                <a
                  href={getWhatsAppBookingLink()}
                  target="_blank"
                  rel="noopener noreferrer"
                >
                  <Button className="bg-[#25D366] hover:bg-[#20BA5A] text-white px-8 py-6 text-lg flex items-center space-x-2">
                    <MessageCircle className="h-5 w-5" />
                    <span>Book via WhatsApp</span>
                  </Button>
                </a>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Main Content */}
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-16">
        <div className="grid lg:grid-cols-3 gap-12">
          {/* Left Column - Main Content */}
          <div className="lg:col-span-2 space-y-12">
            {/* Overview */}
            <section>
              <h2 className="font-serif text-3xl text-[#2C2C2C] mb-6">Overview</h2>
              <p className="text-lg text-[#8B7D6B] leading-relaxed">
                {language === 'en' ? tour.summary : tour.summaryEs}
              </p>
            </section>

            {/* Highlights */}
            <section>
              <h2 className="font-serif text-3xl text-[#2C2C2C] mb-6">Tour Highlights</h2>
              <div className="grid sm:grid-cols-2 gap-4">
                {(language === 'en' ? tour.highlights : tour.highlightsEs).map((highlight, index) => (
                  <div key={index} className="flex items-start space-x-3">
                    <Check className="h-5 w-5 text-[#C4B5A0] flex-shrink-0 mt-1" />
                    <span className="text-[#2C2C2C]">{highlight}</span>
                  </div>
                ))}
              </div>
            </section>

            {/* Day-by-Day Itinerary */}
            <section>
              <h2 className="font-serif text-3xl text-[#2C2C2C] mb-8">Detailed Itinerary</h2>
              <div className="space-y-6">
                {tour.itinerary.map((day) => (
                  <div key={day.day} className="bg-white rounded-lg p-6 shadow-md border-l-4 border-[#C4B5A0]">
                    <h3 className="font-serif text-xl text-[#2C2C2C] mb-2">
                      Day {day.day} — {language === 'en' ? day.title : day.titleEs}
                    </h3>
                    <p className="text-[#8B7D6B]">
                      {language === 'en' ? day.description : day.descriptionEs}
                    </p>
                  </div>
                ))}
              </div>
            </section>

            {/* Inclusions & Exclusions */}
            <section>
              <h2 className="font-serif text-3xl text-[#2C2C2C] mb-8">What's Included</h2>
              <div className="grid md:grid-cols-2 gap-8">
                {/* Inclusions */}
                <div>
                  <h3 className="font-medium text-lg text-[#2C2C2C] mb-4 flex items-center">
                    <Check className="h-5 w-5 text-green-600 mr-2" />
                    Inclusions
                  </h3>
                  <ul className="space-y-2">
                    {(language === 'en' ? tour.inclusions : tour.inclusionsEs).map((item, index) => (
                      <li key={index} className="flex items-start space-x-2">
                        <Check className="h-4 w-4 text-[#C4B5A0] flex-shrink-0 mt-1" />
                        <span className="text-[#8B7D6B] text-sm">{item}</span>
                      </li>
                    ))}
                  </ul>
                </div>

                {/* Exclusions */}
                <div>
                  <h3 className="font-medium text-lg text-[#2C2C2C] mb-4 flex items-center">
                    <X className="h-5 w-5 text-red-600 mr-2" />
                    Exclusions
                  </h3>
                  <ul className="space-y-2">
                    {(language === 'en' ? tour.exclusions : tour.exclusionsEs).map((item, index) => (
                      <li key={index} className="flex items-start space-x-2">
                        <X className="h-4 w-4 text-[#8B7D6B] flex-shrink-0 mt-1" />
                        <span className="text-[#8B7D6B] text-sm">{item}</span>
                      </li>
                    ))}
                  </ul>
                </div>
              </div>
            </section>

            {/* Gallery */}
            <section>
              <h2 className="font-serif text-3xl text-[#2C2C2C] mb-8">Photo Gallery</h2>
              <div className="relative">
                <img
                  src={typeof tour.gallery[currentImageIndex] === 'string' ? tour.gallery[currentImageIndex] : tour.gallery[currentImageIndex].url}
                  alt={typeof tour.gallery[currentImageIndex] === 'string' ? `${tour.name} gallery ${currentImageIndex + 1}` : tour.gallery[currentImageIndex].alt}
                  className="w-full h-[400px] object-cover rounded-lg"
                />
                
                {/* Gallery Navigation */}
                <button
                  onClick={prevImage}
                  className="absolute left-4 top-1/2 -translate-y-1/2 bg-white/80 hover:bg-white p-2 rounded-full shadow-lg transition-colors"
                  aria-label="Previous image"
                >
                  <ChevronLeft className="h-6 w-6 text-[#2C2C2C]" />
                </button>
                <button
                  onClick={nextImage}
                  className="absolute right-4 top-1/2 -translate-y-1/2 bg-white/80 hover:bg-white p-2 rounded-full shadow-lg transition-colors"
                  aria-label="Next image"
                >
                  <ChevronRight className="h-6 w-6 text-[#2C2C2C]" />
                </button>

                {/* Thumbnails */}
                <div className="flex space-x-2 mt-4 overflow-x-auto">
                  {tour.gallery.map((img, index) => (
                    <button
                      key={index}
                      onClick={() => setCurrentImageIndex(index)}
                      className={`flex-shrink-0 w-20 h-20 rounded-md overflow-hidden border-2 ${
                        index === currentImageIndex ? 'border-[#C4B5A0]' : 'border-transparent'
                      }`}
                    >
                      <img 
                        src={typeof img === 'string' ? img : img.url} 
                        alt={typeof img === 'string' ? `Thumbnail ${index + 1}` : img.alt} 
                        className="w-full h-full object-cover" 
                      />
                    </button>
                  ))}
                </div>
              </div>
            </section>

            {/* FAQ */}
            <section>
              <h2 className="font-serif text-3xl text-[#2C2C2C] mb-8">Frequently Asked Questions</h2>
              <div className="space-y-4">
                {tour.faqs.map((faq, index) => (
                  <div key={index} className="bg-white rounded-lg p-6 shadow-sm">
                    <h3 className="font-medium text-lg text-[#2C2C2C] mb-2">
                      {language === 'en' ? faq.question : faq.questionEs}
                    </h3>
                    <p className="text-[#8B7D6B]">
                      {language === 'en' ? faq.answer : faq.answerEs}
                    </p>
                  </div>
                ))}
              </div>
            </section>
          </div>

          {/* Right Column - Sidebar */}
          <div className="lg:col-span-1">
            <div className="sticky top-24 space-y-8">
              {/* Price on Request Card */}
              <div className="bg-white rounded-lg p-6 shadow-lg border border-[#F5F3F0]">
                <h3 className="font-serif text-2xl text-[#2C2C2C] mb-4">Request Pricing</h3>
                <div className="mb-6">
                  <p className="text-2xl font-medium text-[#6B4E37] italic mb-2">
                    Price on request
                  </p>
                  <p className="text-sm text-[#8B7D6B]">
                    Contact us for personalized pricing based on your travel dates, group size, and preferences.
                  </p>
                </div>
                
                <div className="space-y-3 mb-6">
                  <p className="text-sm text-[#8B7D6B]">
                    <strong>Special Offers Available:</strong><br />
                    {language === 'en' ? tour.pricing.groupDiscount : tour.pricing.groupDiscountEs}
                  </p>
                  <p className="text-xs text-[#8B7D6B]">
                    {language === 'en' ? tour.pricing.note : tour.pricing.noteEs}
                  </p>
                </div>

                <a href={getWhatsAppBookingLink()} target="_blank" rel="noopener noreferrer">
                  <Button className="w-full bg-[#25D366] hover:bg-[#20BA5A] text-white flex items-center justify-center space-x-2 mb-3">
                    <MessageCircle className="h-5 w-5" />
                    <span>Get Quote on WhatsApp</span>
                  </Button>
                </a>
                
                <Button 
                  onClick={() => document.getElementById('booking-form')?.scrollIntoView({ behavior: 'smooth' })}
                  className="w-full bg-[#C4B5A0] hover:bg-[#6B4E37] text-white"
                >
                  Request Detailed Itinerary
                </Button>
              </div>

              {/* Booking Form */}
              <div id="booking-form" className="bg-white rounded-lg p-6 shadow-lg border border-[#F5F3F0]">
                <h3 className="font-serif text-2xl text-[#2C2C2C] mb-6">Book This Tour</h3>
                <form onSubmit={handleBookingSubmit} className="space-y-4">
                  <div>
                    <Label htmlFor="name">Full Name *</Label>
                    <Input
                      id="name"
                      name="name"
                      value={bookingForm.name}
                      onChange={handleFormChange}
                      required
                      className="border-[#D4C4B0] focus:border-[#C4B5A0]"
                    />
                  </div>
                  
                  <div>
                    <Label htmlFor="email">Email *</Label>
                    <Input
                      id="email"
                      name="email"
                      type="email"
                      value={bookingForm.email}
                      onChange={handleFormChange}
                      required
                      className="border-[#D4C4B0] focus:border-[#C4B5A0]"
                    />
                  </div>
                  
                  <div>
                    <Label htmlFor="phone">WhatsApp Number *</Label>
                    <Input
                      id="phone"
                      name="phone"
                      type="tel"
                      value={bookingForm.phone}
                      onChange={handleFormChange}
                      required
                      className="border-[#D4C4B0] focus:border-[#C4B5A0]"
                    />
                  </div>
                  
                  <div>
                    <Label htmlFor="dates">Travel Dates</Label>
                    <Input
                      id="dates"
                      name="dates"
                      type="text"
                      placeholder="e.g., March 15-22, 2025"
                      value={bookingForm.dates}
                      onChange={handleFormChange}
                      className="border-[#D4C4B0] focus:border-[#C4B5A0]"
                    />
                  </div>
                  
                  <div>
                    <Label htmlFor="travelers">Number of Travelers *</Label>
                    <Input
                      id="travelers"
                      name="travelers"
                      type="number"
                      min="1"
                      value={bookingForm.travelers}
                      onChange={handleFormChange}
                      required
                      className="border-[#D4C4B0] focus:border-[#C4B5A0]"
                    />
                  </div>
                  
                  <div>
                    <Label htmlFor="message">Additional Message</Label>
                    <Textarea
                      id="message"
                      name="message"
                      rows={3}
                      value={bookingForm.message}
                      onChange={handleFormChange}
                      className="border-[#D4C4B0] focus:border-[#C4B5A0]"
                    />
                  </div>

                  <Button type="submit" className="w-full bg-[#C4B5A0] hover:bg-[#6B4E37] text-white">
                    Submit & Open WhatsApp
                  </Button>
                </form>
              </div>
            </div>
          </div>
        </div>

        {/* Related Tours */}
        {relatedToursList.length > 0 && (
          <section className="mt-16 pt-16 border-t border-[#F5F3F0]">
            <h2 className="font-serif text-3xl text-[#2C2C2C] mb-8 text-center">You May Also Like</h2>
            <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8">
              {relatedToursList.map((relatedTour) => (
                <Link
                  key={relatedTour.id}
                  to={`/tours/${relatedTour.slug}`}
                  className="group block"
                >
                  <div className="relative overflow-hidden rounded-lg mb-4">
                    <img
                      src={relatedTour.heroImage}
                      alt={language === 'en' ? relatedTour.name : relatedTour.nameEs}
                      className="w-full h-64 object-cover transform group-hover:scale-110 transition-transform duration-700"
                    />
                    <div className="absolute inset-0 bg-gradient-to-t from-black/60 to-transparent"></div>
                    <div className="absolute bottom-4 left-4 right-4">
                      <h3 className="font-serif text-xl text-white">
                        {language === 'en' ? relatedTour.name : relatedTour.nameEs}
                      </h3>
                      <p className="text-white/80 text-sm">{language === 'en' ? relatedTour.duration : relatedTour.durationEs}</p>
                    </div>
                  </div>
                </Link>
              ))}
            </div>
          </section>
        )}
      </div>

      <Footer />
    </div>
  );
};
